/**
 * Tiny DOM helpers used by the notification hooks.
 *
 * Every accessor is paranoid about runtime support: SSR, iOS Safari < 16.4,
 * insecure contexts, and locked-down WebViews can all be missing the
 * `Notification` API or the Page Visibility API. None of these helpers throw.
 */

export interface BrowserNotificationOptions {
  body: string;
  /** Coalesces successive notifications. Use the thread id. */
  tag: string;
  /** Optional icon URL. Falls back to the favicon. */
  icon?: string;
  /** Optional click handler — fires when the user activates the notification. */
  onClick?: () => void;
}

/** True only in a browser-like environment with `window` available. */
export function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/** True when the page is currently hidden (background tab, minimised, etc.). */
export function isDocumentHidden(): boolean {
  if (!hasWindow()) return false;
  // `document.hidden` is the broadest-supported flag; `visibilityState`
  // gives us belt-and-braces coverage in case `hidden` is unreliable.
  return document.hidden || document.visibilityState !== 'visible';
}

/** True only when the Notification API is usable in this environment. */
export function isNotificationApiSupported(): boolean {
  return hasWindow() && 'Notification' in window;
}

/** Safe read of the current permission. `'unsupported'` if the API is missing. */
export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function readNotificationPermission(): NotificationPermissionState {
  if (!isNotificationApiSupported()) return 'unsupported';
  try {
    return window.Notification.permission;
  } catch {
    return 'unsupported';
  }
}

/** Request permission, with feature detection and error swallowing. */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isNotificationApiSupported()) return 'unsupported';
  try {
    const result = await window.Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

/**
 * Construct a browser notification. Returns the underlying instance so the
 * caller can `.close()` it if needed; returns `null` when not supported, when
 * permission is missing, or when the constructor throws (some Linux WMs).
 */
export function spawnNotification(
  title: string,
  options: BrowserNotificationOptions,
): Notification | null {
  if (!isNotificationApiSupported()) return null;
  if (readNotificationPermission() !== 'granted') return null;
  try {
    const n = new window.Notification(title, {
      body: options.body,
      tag: options.tag,
      icon: options.icon,
      // Renotify only fires the OS chrome if the tag already exists. Default
      // false — the visual unread badges in-app handle re-arrival cues.
    });
    if (options.onClick) {
      const handler = options.onClick;
      n.onclick = (ev) => {
        ev.preventDefault();
        try {
          window.focus();
        } catch {
          // Ignore — some browsers block window.focus() from notifications.
        }
        handler();
        n.close();
      };
    }
    return n;
  } catch {
    return null;
  }
}

/** Clamp an unread count to a max-width display string (e.g. "99+"). */
export function formatUnreadCount(n: number, max = 99): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n > max) return `${max}+`;
  return String(Math.floor(n));
}
